import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from './contexts/ThemeContext'
import { DataExplorerProvider } from './contexts/DataExplorerContext'
import { FormatLocaleProvider } from './contexts/FormatLocaleContext'
import { Toaster } from './components/ui/sonner'
import './index.css'
import App from './App.tsx'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: any) => {
        if (error?.code === 404) {
          return false
        }
        return failureCount < 3
      },
    },
  },
})
import Home from './pages/Home.tsx'
import AuthenticatedRootPage from './pages/AuthenticatedRootPage.tsx'
import UnauthenticatedRootPage from './pages/UnauthenticatedRootPage.tsx'
import Login from './pages/Login.tsx'
import Agents from './pages/Agents.tsx'
import Data from './pages/Data.tsx'
import Sources from './pages/data/Sources.tsx'
import SourceDetail from './pages/data/SourceDetail.tsx'
import Cleaning from './pages/data/Cleaning.tsx'
import Metrics from './pages/data/Metrics.tsx'
import Entities from './pages/data/Entities.tsx'
import KnowledgeGraph from './pages/data/KnowledgeGraph.tsx'
import Context from './pages/data/Context.tsx'
import Artifacts, { ArtifactDetail } from './pages/Artifacts.tsx'
import KnowledgeBase from './pages/KnowledgeBase.tsx'
import Insights from './pages/Insights.tsx'
import Reports from './pages/analysis/Reports.tsx'
import Charts from './pages/analysis/Charts.tsx'
import ChartDetail from './pages/analysis/ChartDetail.tsx'
import Dashboards from './pages/analysis/Dashboards.tsx'
import DashboardDetail from './pages/analysis/DashboardDetail.tsx'
import AskEllie from './pages/analysis/AskEllie.tsx'
import ConversationPage from './pages/ellie/ConversationPage.tsx'
import Develop from './pages/develop/Develop.tsx'
import Nodes from './pages/develop/Nodes.tsx'
import NodeDetail from './pages/develop/NodeDetail.tsx'
import EntityDetail from './pages/develop/EntityDetail.tsx'
import DevelopEntities from './pages/develop/Entities.tsx'
import DevelopMetrics from './pages/develop/Metrics.tsx'
import MetricDetail from './pages/develop/MetricDetail.tsx'
import Dimensions from './pages/develop/Dimensions.tsx'
import DimensionDetail from './pages/develop/DimensionDetail.tsx'
import DangerZone from './pages/develop/DangerZone.tsx'
import SemanticGaps from './pages/develop/SemanticGaps.tsx'
import Analytics from './pages/analysis/Analytics.tsx'
import ElliePage from './pages/Ellie.tsx'
import RuthiePage from './pages/Ruthie.tsx'
import RuthieConversationPage from './pages/ruthie/ConversationPage.tsx'
import Glossary from './pages/Glossary.tsx'
import SemanticLayer from './pages/SemanticLayer.tsx'
import Diagram from './pages/Diagram.tsx'
import SettingsPage from './pages/Settings.tsx'
import UsersPage from './pages/Users.tsx'
import InstallTemplates from './pages/templates/InstallTemplates.tsx'
import MyTemplates from './pages/templates/MyTemplates.tsx'
import ExportTemplate from './pages/templates/ExportTemplate.tsx'
import TenantArchived from './pages/TenantArchived.tsx'
import ExtLogin from './pages/ExtLogin.tsx'
import SystemSession from './pages/SystemSession.tsx'
import SecureConfigFillPage from './pages/SecureConfigFillPage.tsx'
import FlatFileUploadPage from './pages/FlatFileUploadPage.tsx'
import AuthCompletePage from './pages/AuthCompletePage.tsx'
import Embed from './pages/Embed.tsx'
import Logout from './pages/Logout.tsx'
import TenantNotFound from './pages/TenantNotFound.tsx'
import SsoPrelogin from './pages/SsoPrelogin.tsx'
import DocsLayout from './pages/docs/DocsLayout.tsx'
import DocPage from './pages/docs/DocPage.tsx'

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        element: <AuthenticatedRootPage />,
        children: [
          {
            index: true,
            element: <Home />,
          },
          {
            path: 'ellie',
            element: <ElliePage />,
          },
          {
            path: 'ruthie',
            element: <RuthiePage />,
          },
          {
            path: 'agents',
            element: <Agents />,
          },
          {
            path: 'semantic-layer',
            element: <SemanticLayer />,
            children: [
              {
                index: true,
                element: <Navigate to="/semantic-layer/glossary" replace />,
              },
              {
                path: 'glossary',
                element: <Glossary />,
              },
              {
                path: 'diagram',
                element: <Diagram />,
              },
            ],
          },
          {
            path: 'glossary',
            element: <Navigate to="/semantic-layer/glossary" replace />,
          },
          {
            path: 'knowledge-base',
            element: <KnowledgeBase />,
          },
          {
            path: 'knowledge-base/article/:id',
            element: <KnowledgeBase />,
          },
          {
            path: 'artifacts',
            element: <Artifacts />,
          },
          {
            path: 'artifacts/:id',
            element: <ArtifactDetail />,
          },
          {
            path: 'settings',
            element: <SettingsPage />,
          },
          {
            path: 'users',
            element: <UsersPage />,
          },
          {
            path: 'sources',
            element: <Sources />,
          },
          {
            path: 'sources/:sourceId',
            element: <SourceDetail />,
          },
          {
            path: 'sources/:sourceId/:nodeId',
            element: <SourceDetail />,
          },
          {
            path: 'data',
            element: <Data />,
            children: [
              {
                index: true,
                element: <Navigate to="/data/cleaning" replace />,
              },
              {
                path: 'cleaning',
                element: <Cleaning />,
              },
              {
                path: 'metrics',
                element: <Metrics />,
              },
              {
                path: 'entities',
                element: <Entities />,
              },
              {
                path: 'knowledge-graph',
                element: <KnowledgeGraph />,
              },
              {
                path: 'context',
                element: <Context />,
              },
            ],
          },
          {
            path: 'insights',
            element: <Insights />,
            children: [
              {
                index: true,
                element: <Navigate to="/insights/reports" replace />,
              },
              {
                path: 'reports',
                element: <Reports />,
              },
              {
                path: 'charts/:chartId',
                element: <ChartDetail />,
              },
              {
                path: 'charts',
                element: <Charts />,
              },
              {
                path: 'dashboards/:dashboardId',
                element: <DashboardDetail />,
              },
              {
                path: 'dashboards',
                element: <Dashboards />,
              },
              {
                path: 'ask-ellie',
                element: <AskEllie />,
              },
            ],
          },
          {
            path: 'ellie/conversation/:id',
            element: <ConversationPage />,
          },
          {
            path: 'ruthie/conversation/:id',
            element: <RuthieConversationPage />,
          },
          {
            path: 'analytics',
            element: <Analytics />,
          },
          {
            path: 'analytics/charts/:chartId',
            element: <ChartDetail />,
          },
          {
            path: 'analytics/dashboards/:dashboardId',
            element: <DashboardDetail />,
          },
          {
            path: 'develop',
            element: <Develop />,
            children: [
              {
                index: true,
                element: <Navigate to="/develop/modeling" replace />,
              },
              {
                path: 'modeling',
                element: <Nodes />,
              },
              {
                path: 'modeling/:id',
                element: <NodeDetail />,
              },
              {
                path: 'entities',
                element: <DevelopEntities />,
              },
              {
                path: 'entities/:id',
                element: <EntityDetail />,
              },
              {
                path: 'metrics',
                element: <DevelopMetrics />,
              },
              {
                path: 'metrics/:id',
                element: <MetricDetail />,
              },
              {
                path: 'dimensions',
                element: <Dimensions />,
              },
              {
                path: 'dimensions/:id',
                element: <DimensionDetail />,
              },
              {
                path: 'gaps',
                element: <SemanticGaps />,
              },
              {
                path: 'danger-zone',
                element: <DangerZone />,
              },
            ],
          },
          {
            // Redirect legacy /dashboards/:id URLs (used by screengrabber) to new path
            path: 'dashboards/:dashboardId',
            element: <DashboardDetail />,
          },
          {
            path: 'templates/install',
            element: <InstallTemplates />,
          },
          {
            path: 'templates/admin',
            element: <MyTemplates />,
          },
          {
            path: 'templates/export',
            element: <ExportTemplate />,
          },
          {
            path: 'templates/edit/:templateId',
            element: <ExportTemplate />,
          },
        ],
      },
      {
        path: 'do/logout',
        element: <Logout />,
      },
      {
        path: 'logout',
        element: <Navigate to="/do/logout" replace />,
      },
      {
        path: 'tenant-archived',
        element: <TenantArchived />,
      },
      {
        path: 'tenant-not-found',
        element: <TenantNotFound />,
      },
      {
        path: 'system-session',
        element: <SystemSession />,
      },
      {
        path: 'sso-prelogin',
        element: <SsoPrelogin />,
      },
      {
        path: 'source/secure-config/:code',
        element: <SecureConfigFillPage />,
      },
      {
        path: 'source/flat-file-upload/:code',
        element: <FlatFileUploadPage />,
      },
      {
        path: 'source/auth-complete',
        element: <AuthCompletePage />,
      },
      {
        // Embedded-analytics iframe landing. Exchanges the one-time code
        // (from POST /api/v1/ext/init-session) for a JWT and redirects to
        // the target (e.g. /insights/dashboards/<id>). See src/pages/Embed.tsx.
        path: 'embed',
        element: <Embed />,
      },
      {
        path: 'docs',
        element: <DocsLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/docs/connecting-to-snowflake" replace />,
          },
          {
            path: ':slug',
            element: <DocPage />,
          },
        ],
      },
      {
        element: <UnauthenticatedRootPage />,
        children: [
          {
            path: 'login',
            element: <Login />,
          },
          {
            path: 'ext-login',
            element: <ExtLogin />,
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
])

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <FormatLocaleProvider>
          <DataExplorerProvider>
            <RouterProvider router={router} />
            <Toaster />
          </DataExplorerProvider>
        </FormatLocaleProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
)
